import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface ProductTest {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  test_name: string;
  test_description: string;
  test_code: string;
  test_type: string;
  test_price: string;
  discount_price: number;
  sample_type: string;
  turnaround_time: string;
  parameters_count: number;
  parameter_details: string;
  is_package: boolean;
  tags: string;
  preparation_notes: string;
  age_group: string;
  gender: string;
  is_active: boolean;
  product_checkup_id: number;
}

interface TestsState {
  tests: ProductTest[];
  selectedTest: ProductTest | null;
  loading: boolean;
  error: string | null;
}

const initialState: TestsState = {
  tests: [],
  selectedTest: null,
  loading: false,
  error: null,
};

export const fetchTests = createAsyncThunk(
  'tests/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.pms}/api/v1/tests`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch tests');
    }
  }
);

export const fetchTestById = createAsyncThunk(
  'tests/fetchById',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.pms}/api/v1/tests/${id}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch test');
    }
  }
);

export const createTest = createAsyncThunk(
  'tests/create',
  async (data: Partial<ProductTest>, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.pms}/api/v1/tests`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create test');
    }
  }
);

export const updateTest = createAsyncThunk(
  'tests/update',
  async ({ id, data }: { id: number; data: Partial<ProductTest> }, thunkAPI) => {
    try {
      const response = await apiClient.put(`${config.services.pms}/api/v1/tests/${id}`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to update test');
    }
  }
);

export const deleteTest = createAsyncThunk(
  'tests/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.pms}/api/v1/tests/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete test');
    }
  }
);

const testsSlice = createSlice({
  name: 'tests',
  initialState,
  reducers: {
    clearSelectedTest: (state) => { state.selectedTest = null; },
    clearTestsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTests.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTests.fulfilled, (state, action) => { state.loading = false; state.tests = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchTests.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createTest.pending, (state) => { state.loading = true; })
      .addCase(createTest.fulfilled, (state, action) => {
        state.loading = false;
        const newTest = action.payload?.data || action.payload;
        if (newTest) state.tests.push(newTest);
      })
      .addCase(createTest.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(updateTest.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.tests.findIndex(t => t.ID === updated.ID);
          if (idx !== -1) state.tests[idx] = updated;
        }
      })
      .addCase(deleteTest.fulfilled, (state, action) => {
        state.tests = state.tests.filter(t => t.ID !== action.payload);
      });
  },
});

export const { clearSelectedTest, clearTestsError } = testsSlice.actions;
export default testsSlice.reducer;
